import { isArray } from 'lodash';
import { send, validateSerial } from '../../common/utils';
import { HUB_SCENES_LIST } from '../../common/methods';

/**
 * @typedef {Object} HubScenesListFilters
 * @property {Object} [include] - Filters to include scenes.
 * @property {Object} [include.generator] - Filter by generator properties.
 * @property {string} [include.generator.role] - Generator role filter (e.g. 'all').
 */

/**
 * @typedef {Object} HubScenesListParams
 * @property {HubScenesListFilters} [filters] - Optional filters for the scenes list.
 */

/**
 * Sends a request to the Hub to retrieve the list of scenes.
 * This is the low-level API function that validates the serial and sends the WebSocket message.
 *
 * @param {string} serial - The serial number of the Hub.
 * @param {HubScenesListParams} params - Optional parameters for filtering scenes.
 * @param {Function} onSuccess - A callback function that executes when the request is successful.
 * @param {Function} onError - A callback function that executes when the request fails.
 *
 * @throws {Error} Throws an error if "serial" is not valid.
 * @returns {void}
 * @see {[hub.scenes.list](https://log.ezlo.com/new/hub/scenes/local_scenes_api/#hubsceneslist)}
 */
export const hubScenesListApi = (serial, params, onSuccess, onError) => {
    validateSerial(serial);

    return send(serial, HUB_SCENES_LIST, params, onSuccess, onError);
};

/**
 * Retrieves the list of scenes from the Hub as a Promise.
 * Resolves with the array of scenes, or rejects if the response is missing or malformed.
 *
 * @param {string} serial - The serial number of the Hub.
 * @param {HubScenesListParams} [params={}] - Optional parameters for filtering scenes.
 *
 * @throws {Error} Throws an error if "serial" is not valid.
 * @returns {Promise<Array>} Resolves with an array of scene objects.
 * @see {[hub.scenes.list](https://log.ezlo.com/new/hub/scenes/local_scenes_api/#hubsceneslist)}
 */
export const list = (serial, params = {}) =>
    new Promise((resolve, reject) => {
        hubScenesListApi(
            serial,
            params,
            (result) => {
                if (isArray(result?.scenes)) {
                    resolve(result.scenes);
                } else {
                    reject(new Error('API error. Missing scene list'));
                }
            },
            reject,
        ).catch(reject);
    });
